import Button from '@axa-japan/design-system-react/Button';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon, ErrorIcon, WarningIcon } from '@axa-japan/design-system-react/Icons';
import Message from '@axa-japan/design-system-react/Message';
import Modal from '@axa-japan/design-system-react/Modal';
import Text from '@axa-japan/design-system-react/Text';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FILE_DEFAULT_NAME } from '../utils/fileUtils';

import StatusModal from '@axa-japan/design-system-react/StatusModal';
import DocumentUpload from '../components/DocumentUpload/DocumentUpload';
import UserGuideUploadModal from '../components/UserGuideUploadModal';
import { useGetDocuments } from '../hooks/useGetDocuments';
import { useUserContext } from '../providers/UserProvider';
import { DOCUMENT_UPLOAD_PROGRESS } from '../types/session';
import { DocumentType, FormDataType } from '../utils/formUtils';

type UploadFilePageForm = {
  sessionId?: number;
  receptionNo: string;
  formType: string;
  representativeDocs: string[];
  documentsSelected: string[];
  uploadType: string;
  failedUploadDocuments: DocumentType[];
  pendingUploadDocuments: DocumentType[];
};

const UploadFilePageSchema = z.object({
  pendingUploadDocuments: z
    .array(
      z.object({
        documentId: z.string(),
        name: z.string(),
        guidelines: z.string(),
        files: z
          .array(
            z.object({
              fileId: z.string(),
              content: z.instanceof(File),
            }),
          )
          .refine((files) => {
            // Check if at least one file is valid (not a placeholder)
            const hasValidFile = files.some((file) => file.content.name !== FILE_DEFAULT_NAME);
            // Check if all files are placeholders
            const allDefaults = files.every((file) => file.content.name === FILE_DEFAULT_NAME);
            return hasValidFile && !allDefaults;
          }),
      }),
    )
    .min(1, { message: '書類の撮影・ファイル選択をしてください。' }),
});

type UploadFilePageProps = {
  initialData: UploadFilePageForm;
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>;
  onNext: (data: UploadFilePageForm) => void;
  onBack: () => void;
};

const UploadFilePage: React.FC<UploadFilePageProps> = ({ onBack, onNext, initialData, setFormData }) => {
  const [hasError, setHasError] = useState(false);

  const [isUserGuideModalOpen, setIsUserGuideModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { setDocumentUploadStatus } = useUserContext();
  const { data: docs } = useGetDocuments(initialData.sessionId as number);
  // filter documents in both pendingUpload and selected.
  const documents: DocumentType[] =
    initialData.pendingUploadDocuments?.length > 0
      ? initialData.pendingUploadDocuments
      : (docs
          ?.filter((doc) => initialData.documentsSelected?.includes(doc.id))
          .map((doc) => ({
            documentId: doc.id,
            name: doc.name,
            guidelines: doc.guidelines,
            customerUploadRequired: doc.customerUploadRequired,
            files: initialData.failedUploadDocuments.find((pd) => pd.documentId === doc.id)?.files ?? [{ fileId: doc.id, content: undefined }],
          })) ?? []);

  const {
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<UploadFilePageForm>({
    resolver: zodResolver(UploadFilePageSchema),
    mode: 'onSubmit',
    defaultValues: { ...initialData, pendingUploadDocuments: documents },
  });

  // Modal is having weird behavior if set false by default
  useEffect(() => {
    setIsUserGuideModalOpen(documents.some((document) => document.files.some((file) => file.content === undefined)) || documents.length === 0);
  }, []);

  const handleDocumentUpdate = (document: DocumentType) => {
    const foundIndex = documents.findIndex((doc) => doc.documentId === document.documentId);
    if (foundIndex >= 0) documents[foundIndex] = document;
    setFormData((prev) => ({ ...prev, pendingUploadDocuments: documents }));

    // Enable global close button modal if any document has at least 1 file ready to upload
    const hasFilesWithContent = documents.some((doc) =>
      doc.files.some((file) => {
        return file.content && file.content?.size > 0;
      }),
    );

    setHasError(false);
    clearErrors();

    setDocumentUploadStatus(hasFilesWithContent ? DOCUMENT_UPLOAD_PROGRESS.inProgress : null);
    clearErrors();

    return documents;
  };

  const onSubmit: SubmitHandler<UploadFilePageForm> = (formData) => {
    onNext({ ...formData });
  };

  const onError = () => {
    setHasError(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onReturnBack = () => {
    if (documents.some((document) => document.files?.some((file) => file.content && file.content.size > 0))) {
      setIsDeleteModalOpen(true);
      return;
    }
    onReturn();
  };

  const onReturn = () => {
    setFormData((prev) => ({
      ...prev,
      pendingUploadDocuments: [],
    }));
    // Reset global close button
    setDocumentUploadStatus(null);
    onBack();
  };

  const showValidationError = (document: DocumentType) => {
    if (document.files.length === 1) {
      return !document.files[0].content || document.files[0].content.size === 0;
    }

    return false;
  };

  return (
    <form className="flex flex-col gap-10" onSubmit={handleSubmit(onSubmit, onError)}>
      <Modal
        id="default-modal"
        open={isUserGuideModalOpen}
        setOpen={setIsUserGuideModalOpen}
        title="撮影時のご注意"
        content={<UserGuideUploadModal />}
        buttons={[
          {
            text: 'OK',
            onClick: () => setIsUserGuideModalOpen(false),
          },
        ]}
      />

      <StatusModal
        id="warning-delete-progress-modal"
        title="撮影したファイルはまだ送信されていません"
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        content="戻った場合、撮影・ファイル選択したファイルはすべて削除されますがよろしいでしょうか。"
        buttons={[
          {
            text: 'キャンセル',
          },
          {
            text: 'OK',
            onClick: () => {
              onReturn();
            },
          },
        ]}
      />

      <div className="flex flex-col gap-4">
        <Heading as="h1">書類の撮影・ファイル選択</Heading>
        {Object.keys(errors).length > 0 && (
          <Message title="入力内容をご確認ください" status="danger" hasCloseButton={false}>
            書類の撮影・ファイル選択をしてください。
          </Message>
        )}
        <div>
          <Text>撮影する場合は、1枚ずつ撮影してください。</Text>
          <Text>ファイル選択の場合は、画像（JPG・JPEG・PNG・TIFF）またはPDFのみ選択できます。</Text>
          <Text>
            PDF・TIFFのファイルを指定する場合、ファイル内に複数ページがあっても指定可能です。ただし、1ファイルに複数の書類の混在はできません。
          </Text>
        </div>
        <div className="bg-statusInformationLight p-7">
          {documents.map((document, index) => (
            <Heading key={`document-header-name-${document.documentId}`}>
              {index + 1}.{document.name}
            </Heading>
          ))}
        </div>
        <div className="flex flex-col">
          <div className="flex flex-col">
            <Text className="flex flex-row">
              <span className="inline-block text-lg">
                <WarningIcon isOutline size="medium" />
              </span>
              1画像・ファイルあたり20MB以下で撮影・選択してください。
            </Text>
            <Text className="flex flex-row">
              <span className="inline-block text-lg">
                <WarningIcon isOutline size="medium" />
              </span>
              1書類につき最大30ファイルまで撮影またはファイル選択いただけます。
            </Text>
            <Text className="flex flex-row">
              <span className="inline-block text-lg">
                <WarningIcon isOutline size="medium" />
              </span>
              撮影またはファイル選択した書類を確認してください。すべての書類の撮影またはファイルを選択後、送信画面に進んでください。
            </Text>
          </div>
        </div>
        <p className="text-axaBlue flex flex-row cursor-pointer" onClick={() => setIsUserGuideModalOpen(true)}>
          <ErrorIcon isOutline />
          撮影時の注意事項
        </p>
      </div>

      <Controller
        name={'pendingUploadDocuments'}
        control={control}
        render={({ field: { onChange } }) => (
          <>
            {documents.map((document, index) => {
              return (
                <DocumentUpload
                  key={document.documentId}
                  receptionNo={initialData.receptionNo}
                  document={document}
                  header={`${index + 1}. ${document.name}`}
                  onFileChange={(files) => {
                    onChange(handleDocumentUpdate({ ...document, files }));
                  }}
                  hasError={hasError && showValidationError(document)}
                />
              );
            })}
          </>
        )}
      />

      <div className="flex justify-center gap-3 mt-5">
        <div className="w-80">
          <Button type="button" variant="outlined" onClick={() => onReturnBack()} fullWidth icon={<ArrowIcon rotation="180" />} iconPosition="left">
            戻る
          </Button>
        </div>
        <div className="w-80">
          <Button type="submit" fullWidth icon={<ArrowIcon />}>
            画像を確認する
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UploadFilePage;
